import { mapActions, mapGetters } from 'vuex';
import UserMixin from '@/modules/user/mixins/UserMixin';
import QuantityDisplayMixin from '@/modules/orders/mixins/QuantityDisplayMixin';
import CartItemControls from '@/modules/cart/mixins/CartControls/CartItemControls';
import { ENTRY_METHOD } from '@/models/entities/cart/entry-method';
import CartControls from '@/modules/cart/CartControls';

export default {
  props: {
    item: {
      type: Object,
    },
    editingItem: {
      type: Boolean,
    },
    variation: {
      type: String,
    },
    cartInstructions: {
      type: String,
    },
  },
  mixins: [CartItemControls, UserMixin, QuantityDisplayMixin],
  data() {
    return { cartControls: null };
  },
  created() {
    this.cartControls = new CartControls({
      translate: this.$t,
      storeConfigurationGetter: () => this.$configuration,
      cartRequestVuexGetter: () => this.cart,
      nonEmptyCartVuexGetter: () => this.nonEmptyCarts,
      lineItemsVuexGetter: () => this.getLineItems,
      giftCardLineItemsVuexGetter: () => this.getGiftCardItems,
      cartIsGiftCardVuexGetter: () => this.isGiftCard,
      userVuexGetter: () => this.getUser,
      customerModeScanStateVuexGetter: () => this.isCustomerModeScan,
      setItemQuantityVuexAction: async parameters => this.setItemQuantity(parameters),
      getCartsVuexAction: async () => this.getCarts(),
      setCartsLoadingStatusVuexAction: async status => this.setCartsLoadingStatus(status),
      removeEGiftCardVuexAction: async giftCard => this.removeEGiftCard(giftCard),
      orderTypePropGetter: () => this.orderType,
    });
  },
  computed: {
    ...mapGetters({
      configuration: 'getConfiguration',
      isSyncing: 'cart/isSyncing',
      isGiftCard: 'cart/isGiftCard',
      getUser: 'user/getUser',
      isCustomerModeScan: 'user/isCustomerModeScan',
      cartsCount: 'cart/getCartsCount',
      cart: 'cart/getCartSyncDto',
      nonEmptyCarts: 'cart/nonEmptyCarts',
      getLineItems: 'cart/getLineItems',
      getGiftCardItems: 'cart/getGiftCardItems',
      cartOrderType: 'cart/getCartOrderType',
      isInStore: 'cart/getInStore',
    }),
  },
  methods: {
    ...mapActions({
      setItemQuantity: 'cart/setItemQuantity',
      getCarts: 'cart/getCarts',
      setCartsLoadingStatus: 'cart/setCartsLoadingStatus',
      removeEGiftCard: 'cart/removeGiftCard',
    }),

    getQuantityInCart(item, variation) {
      return this.cartControls.getQuantityInCart(item, variation);
    },

    recoverUnroundedQuantityFromCart(item, variation) {
      return this.cartControls.recoverUnroundedQuantityFromCart(item, variation);
    },

    getWeightedQuantityInCart(item, variation) {
      return this.cartControls.getWeightedQuantityInCart(item, variation);
    },

    getTotalBaseQuantityInCart(item, variation) {
      return this.cartControls.getTotalBaseQuantityInCart(item, variation);
    },

    getTotalBaseQuantity(item, quantity, weightedQuantity) {
      return CartControls.getTotalBaseQuantity(item, quantity, weightedQuantity);
    },

    getQuantityIncrement(item) {
      return CartControls.getQuantityIncrement(item);
    },

    getWeightedQuantityIncrement(item) {
      return CartControls.getWeightedQuantityIncrement(item);
    },

    getDisplayQuantityInCart(item, variation) {
      return this.cartControls.getDisplayQuantityInCart(item, variation);
    },

    isSoldByRandomWeightWithDualAdjusters(item) {
      return CartControls.isSoldByRandomWeightWithDualAdjusters(item);
    },

    getDisplayQuantity(item, quantity) {
      return CartControls.getDisplayQuantity(item, quantity);
    },

    getDisplayRandomWeightFromQuantity(item, quantity) {
      return CartControls.getDisplayRandomWeightFromQuantity(item, quantity);
    },

    getDisplayWeightedQuantityInCart(item, variation) {
      return this.cartControls.getDisplayWeightedQuantityInCart(item, variation);
    },

    getDisplayWeightedQuantity(item, quantity) {
      return CartControls.getDisplayWeightedQuantity(item, quantity);
    },

    getMinimumQuantity(item) {
      return CartControls.getMinimumQuantity(item);
    },

    getMaximumTotalBaseQuantity(item) {
      return this.cartControls.getMaximumTotalBaseQuantity(item);
    },

    getDepartmentMax(item) {
      return CartControls.getDepartmentMax(item);
    },

    getSelectMax(item) {
      return CartControls.getSelectMax(item);
    },

    getOnHand(item) {
      return CartControls.getOnHand(item);
    },

    getMinimumWeightedQuantity(item) {
      return CartControls.getMinimumWeightedQuantity(item);
    },

    isQuantityValidByRacs(item, variation, totalBaseQuantity) {
      return this.cartControls.isQuantityValidByRacs(item, variation, totalBaseQuantity);
    },

    getTotalQuantityForVendorGroup(item) {
      return this.cartControls.getTotalQuantityForVendorGroup(item);
    },

    getRacsPackQuantityInCart(item, variation) {
      return this.cartControls.getRacsPackQuantityInCart(item, variation);
    },

    getRacsPackQuantity(item, totalBaseQuantity) {
      return CartControls.getRacsPackQuantity(item, totalBaseQuantity);
    },

    getNumQuantityIncrements(item, quantity) {
      return CartControls.getNumQuantityIncrements(item, quantity);
    },

    getNextQuantityIncrement(item, currentQuantity) {
      return CartControls.getNextQuantityIncrement(item, currentQuantity);
    },

    getNextQuantityIncrementInCart(item, variation) {
      return this.cartControls.getNextQuantityIncrementInCart(item, variation);
    },

    getNextQuantityDecrement(item, currentQuantity) {
      return CartControls.getNextQuantityDecrement(item, currentQuantity);
    },

    getNextQuantityDecrementInCart(item, variation) {
      return this.cartControls.getNextQuantityDecrementInCart(item, variation);
    },

    isNextQuantityIncrementValid(item, currentQuantity, currentWeightedQuantity) {
      return this.cartControls.isNextQuantityIncrementValid(
        item,
        currentQuantity,
        currentWeightedQuantity,
      );
    },

    isNextQuantityIncrementInCartValid(item, variation) {
      return this.cartControls.isNextQuantityIncrementInCartValid(item, variation);
    },

    isNextQuantityDecrementValid(item, currentQuantity, currentWeightedQuantity) {
      return this.cartControls.isNextQuantityDecrementValid(
        item,
        currentQuantity,
        currentWeightedQuantity,
      );
    },

    isNextQuantityDecrementInCartValid(item, variation) {
      return this.cartControls.isNextQuantityDecrementInCartValid(item, variation);
    },

    getNextWeightedQuantityIncrement(item, currentWeightedQuantity) {
      return CartControls.getNextWeightedQuantityIncrement(item, currentWeightedQuantity);
    },

    getNextWeightedQuantityIncrementInCart(item, variation) {
      return this.cartControls.getNextWeightedQuantityIncrementInCart(item, variation);
    },

    getNextWeightedQuantityDecrement(item, currentWeightedQuantity) {
      return CartControls.getNextWeightedQuantityDecrement(item, currentWeightedQuantity);
    },

    getNextWeightedQuantityDecrementInCart(item, variation) {
      return this.cartControls.getNextWeightedQuantityDecrementInCart(item, variation);
    },

    isNextWeightedQuantityIncrementValid(item, currentQuantity, currentWeightedQuantity) {
      return this.cartControls.isNextWeightedQuantityIncrementValid(
        item,
        currentQuantity,
        currentWeightedQuantity,
      );
    },

    isNextWeightedQuantityIncrementInCartValid(item, variation) {
      return this.cartControls.isNextWeightedQuantityIncrementInCartValid(item, variation);
    },

    isNextWeightedQuantityDecrementValid(item, currentQuantity, currentWeightedQuantity) {
      return this.cartControls.isNextWeightedQuantityDecrementValid(
        item,
        currentQuantity,
        currentWeightedQuantity,
      );
    },

    isNextWeightedQuantityDecrementInCartValid(item, variation) {
      return this.cartControls.isNextWeightedQuantityDecrementInCartValid(item, variation);
    },

    getNextQuantityIncrementWithWeightedQuantityInCart(item, variation) {
      return this.cartControls.getNextQuantityIncrementWithWeightedQuantityInCart(item, variation);
    },

    isQuantityValidByItemLimits(item, quantity, weightedQuantity) {
      return this.cartControls.isQuantityValidByItemLimits(item, quantity, weightedQuantity);
    },

    isQuantityValidIncrement(item, quantity) {
      return CartControls.isQuantityValidIncrement(item, quantity);
    },

    isWeightedQuantityValidIncrement(item, weightedQuantity) {
      return CartControls.isWeightedQuantityValidIncrement(item, weightedQuantity);
    },

    isQuantityValidForCart(item, variation, quantity, weightedQuantity) {
      return this.cartControls.isQuantityValidForCart(item, variation, quantity, weightedQuantity);
    },

    isInStock(item) {
      return CartControls.isInStock(item);
    },

    isInStoreOnly(item) {
      return CartControls.isInStoreOnly(item);
    },

    async setQuantity(
      quantity,
      weightedQuantity,
      item,
      variation,
      priceEmbeddedLineItem,
      showStoreSelectModal = true,
      entryMethod = ENTRY_METHOD.DEFAULT,
    ) {
      return this.cartControls.setQuantity(
        quantity,
        weightedQuantity,
        item,
        variation,
        priceEmbeddedLineItem,
        showStoreSelectModal,
        entryMethod,
      );
    },

    async increment(
      item,
      variation,
      priceEmbeddedLineItem,
      showStoreSelectModal,
      entryMethod = ENTRY_METHOD.DEFAULT,
    ) {
      return this.cartControls.increment(
        item,
        variation,
        priceEmbeddedLineItem,
        showStoreSelectModal,
        entryMethod,
      );
    },

    async decrement(item, variation, priceEmbeddedLineItem) {
      return this.cartControls.decrement(item, variation, priceEmbeddedLineItem);
    },

    async incrementWeightedQuantity(item, variation) {
      return this.cartControls.incrementWeightedQuantity(item, variation);
    },

    async decrementWeightedQuantity(item, variation) {
      return this.cartControls.decrementWeightedQuantity(item, variation);
    },

    async incrementCombinedQuantities(item, variation) {
      return this.cartControls.incrementCombinedQuantities(item, variation);
    },

    async decrementCombinedQuantities(item, variation) {
      return this.cartControls.decrementCombinedQuantities(item, variation);
    },

    getDisplayForCombinedQuantitiesInCart(item, variation) {
      return this.cartControls.getDisplayForCombinedQuantitiesInCart(item, variation);
    },

    async remove(item, variation) {
      return this.cartControls.remove(item, variation);
    },

    async removeGiftCard(giftCardProfile) {
      return this.cartControls.removeGiftCard(giftCardProfile);
    },

    isFirstItemInCartAtNonHomeStore(newQuantity) {
      return this.cartControls.isFirstItemInCartAtNonHomeStore(newQuantity);
    },

    trackAnalyticsNewQuantity(newQuantity, item) {
      return this.cartControls.trackAnalyticsNewQuantity(newQuantity, item);
    },

    createQuantityError(item, quantity, weightedQuantity, totalBaseQuantity) {
      return CartControls.createQuantityError(item, quantity, weightedQuantity, totalBaseQuantity);
    },

    alertItemAddedToCart() {
      return CartControls.alertItemAddedToCart();
    },

    alertMaximumQuantityReached() {
      return CartControls.alertMaximumQuantityReached();
    },

    alertFailedToAddToCart() {
      return CartControls.alertFailedToAddToCart();
    },

    alertGiftCardItem() {
      return this.cartControls.alertGiftCardItem();
    },

    async alertNotShoppingFromHomeStore() {
      return CartControls.alertNotShoppingFromHomeStore();
    },
  },
};
